import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext();

// Generate or get device UUID
const getDeviceUUID = () => {
  let uuid = localStorage.getItem('device_uuid');
  if (!uuid) {
    uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    localStorage.setItem('device_uuid', uuid);
  }
  return uuid;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('cart_items');
    return saved ? JSON.parse(saved) : [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(new Set());
  const [deviceUUID] = useState(getDeviceUUID);

  // Save items to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('cart_items', JSON.stringify(items));
  }, [items]);

  // Decrypt product ID from old_url via backend API
  const decryptProductId = async (oldUrl) => {
    try {
      const response = await axios.post(`${API}/decrypt-product-id?old_url=${encodeURIComponent(oldUrl)}`);
      if (response.data.success) {
        return response.data.product_id;
      }
      return null;
    } catch (error) {
      console.error('Error decrypting product ID:', error);
      return null;
    }
  };

  // Fetch cart from MPX API for a specific store
  const fetchCartForStore = useCallback(async (storeId) => {
    try {
      const response = await axios.get(`${API}/cart/${deviceUUID}/${storeId}`);
      if (response.data.success) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching cart:', error);
      return [];
    }
  }, [deviceUUID]);

  // Add item to cart via MPX API
  const addItem = async (product, merchant) => {
    const productId = product.product_id;

    try {
      setLoading(true);
      setLoadingProducts(prev => new Set(prev).add(productId));
      const storeId = product.store_id;

      // Decrypt product_id from old_url
      let productId = null;
      if (product.old_url) {
        productId = await decryptProductId(product.old_url);
      }

      // Fallback to original product_id if decryption fails
      if (!productId) {
        console.warn('Decryption failed, using original product_id');
        productId = product.product_id;
      }

      console.log('Adding to cart via MPX API:', {
        uuid: deviceUUID,
        store_id: storeId,
        product_id: productId
      });

      // Call backend to add to MPX cart
      const response = await axios.post(`${API}/cart`, {
        uuid: deviceUUID,
        store_id: storeId,
        alias: merchant.alias || product.alias || '',
        product_id: productId,
        qty: 1
      });

      if (response.data.success) {
        // Store session_id per store (multiple sessions support)
        const sessionId = response.data.session_id;

        // Get existing sessions object from localStorage
        const existingSessions = JSON.parse(localStorage.getItem('cart_sessions') || '{}');

        // Update session for this store
        existingSessions[storeId] = sessionId;

        // Save back to localStorage
        localStorage.setItem('cart_sessions', JSON.stringify(existingSessions));

        // Also keep single session_id for backward compatibility (use latest)
        localStorage.setItem('cart_session_id', sessionId);

        // Update local state
        setItems(prev => {
          const existingIndex = prev.findIndex(item =>
            String(item.product_id) === String(productId) && item.store_id === storeId
          );

          if (existingIndex > -1) {
            const updated = [...prev];
            updated[existingIndex].quantity += 1;
            return updated;
          }

          return [...prev, {
            product_id: productId,
            product_name: product.product_name,
            price: product.price,
            quantity: 1,
            img_url: product.img_url,
            store_name: merchant.store_name,
            store_alias: merchant.alias || product.alias,
            store_id: storeId,
            unit: product.unit,
            expired_time: response.data.expired_time // Store expired_time from backend
          }];
        });
      } else {
        throw new Error(response.data.message || 'Failed to add to cart');
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setLoading(false);
      setLoadingProducts(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  // Remove item from cart
  const removeItem = async (productId, storeId) => {
    try {
      setLoading(true);

      // Remove from local state first
      setItems(prev => {
        const updated = prev.filter(item =>
          !(String(item.product_id) === String(productId) && item.store_id === storeId)
        );

        // If no more items from this store, clean up cart_sessions
        const storeHasItems = updated.some(item => item.store_id === storeId);
        if (!storeHasItems) {
          const cartSessions = JSON.parse(localStorage.getItem('cart_sessions') || '{}');
          delete cartSessions[storeId];
          localStorage.setItem('cart_sessions', JSON.stringify(cartSessions));
        }

        return updated;
      });

      // Call backend to remove from D1 + MySQL order_ai_items
      await axios.delete(`${API}/cart/${deviceUUID}/${storeId}/${productId}`);

      // Also remove from MySQL cart-session items
      const cartSessions = JSON.parse(localStorage.getItem('cart_sessions') || '{}');
      const sessionId = cartSessions[storeId];
      if (sessionId) {
        try {
          await axios.delete(`${API}/cart-session/${sessionId}/item/${productId}`);
        } catch (err) {
          console.error('Cart-session item delete error (non-fatal):', err);
        }
      }

    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update item quantity
  const updateQuantity = async (productId, storeId, quantity) => {
    try {
      if (quantity <= 0) {
        await removeItem(productId, storeId);
        return;
      }

      setLoading(true);

      // Update local state
      setItems(prev => prev.map(item =>
        (String(item.product_id) === String(productId) && item.store_id === storeId)
          ? { ...item, quantity }
          : item
      ));

      // Get session_id for this store from localStorage
      const cartSessions = JSON.parse(localStorage.getItem('cart_sessions') || '{}');
      const sessionId = cartSessions[storeId];

      // Call MPX API
      await axios.put(`${API}/cart/${deviceUUID}/${storeId}/${productId}`, { qty: quantity });

      // Also update MySQL cart if we have a session_id
      if (sessionId) {
        const formData = new FormData();
        formData.append('product_id', productId);
        formData.append('quantity', quantity);

        await axios.put(`${API}/cart/${sessionId}/update`, formData);
      }

    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Remove all items from a specific store (UI only, no MySQL delete)
  const removeItemsByStore = (storeId) => {
    setItems(prev => prev.filter(item => item.store_id !== storeId));
  };

  // Clear cart (local only, MPX doesn't have bulk delete)
  const clearCart = async () => {
    try {
      // Remove each item from MPX API
      for (const item of items) {
        await axios.delete(`${API}/cart/${deviceUUID}/${item.store_id}/${item.product_id}`);
      }

      setItems([]);
      localStorage.removeItem('cart_items');
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const getTotal = () => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getItemCount = () => {
    return items.reduce((count, item) => count + item.quantity, 0);
  };

  // Group items by store
  const getItemsByStore = () => {
    const grouped = {};
    items.forEach(item => {
      const key = `${item.store_alias}-${item.store_id}`;
      if (!grouped[key]) {
        grouped[key] = {
          store_name: item.store_name,
          store_alias: item.store_alias,
          store_id: item.store_id,
          items: []
        };
      }
      grouped[key].items.push(item);
    });
    return Object.values(grouped);
  };

  return (
    <CartContext.Provider value={{
      items,
      deviceUUID,
      loading,
      loadingProducts,
      addItem,
      removeItem,
      removeItemsByStore,
      updateQuantity,
      clearCart,
      getTotal,
      getItemCount,
      getItemsByStore,
      fetchCartForStore,
      isCartOpen,
      setIsCartOpen
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
